import { useEffect, useMemo, useState } from "react";
import { Search, PlusCircle } from "lucide-react";
import { Library } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Modal para adicionar um item da Biblioteca Funcional ao plano.
 *
 * Props:
 *  - open, onOpenChange
 *  - context: { type: 'clinical'|'home', phase?: 1|2|3, group?: string }
 *  - onAdd(item): recebe o item escolhido — o chamador decide como inserir no plano.
 */
export default function AddFromLibraryDialog({ open, onOpenChange, context, onAdd }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const params = { type: context?.type, q: q || undefined };
        if (context?.type === "clinical" && context?.phase) params.phase = context.phase;
        if (context?.type === "home" && context?.group) params.group = context.group;
        const data = await Library.list(params);
        setItems(data);
      } finally { setLoading(false); }
    })();
  }, [open, q, context?.type, context?.phase, context?.group]);

  const title = useMemo(() => {
    if (context?.type === "clinical") return `Adicionar técnica · Fase ${context.phase}`;
    if (context?.type === "home") return `Adicionar exercício · ${context.group || "Casa"}`;
    return "Adicionar da biblioteca";
  }, [context]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-slate-950 border-slate-800 text-slate-100" data-testid="add-from-library-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 font-display">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">Filtra por fase/grupo automaticamente. Podes pesquisar por nome, objetivo ou região.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="library-dialog-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Pesquisar…" className="pl-9 bg-slate-900 border-slate-800 text-slate-100" />
        </div>

        <div className="max-h-[60vh] overflow-y-auto space-y-2 mt-2">
          {loading && <div className="text-slate-500 text-sm py-8 text-center">A carregar…</div>}
          {!loading && items.length === 0 && <div className="text-slate-500 text-sm py-8 text-center">Sem resultados.</div>}
          {items.map(it => (
            <button
              key={it.id}
              data-testid={`pick-library-${it.id}`}
              onClick={() => { onAdd?.(it); onOpenChange?.(false); }}
              className="w-full text-left rounded-xl border border-slate-800 bg-slate-900/40 p-4 hover:border-sky-500/40 hover:bg-slate-900/80 transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-slate-50 font-medium text-sm">{it.name}</div>
                  <div className="text-[11px] text-sky-300 mt-0.5">{it.category} {it.body_region ? `· ${it.body_region}` : ""}</div>
                  {it.objective && <div className="text-[11px] text-emerald-400 mt-0.5">{it.objective}</div>}
                  {(it.description || it.instructions) && (
                    <div className="text-[11px] text-slate-400 mt-1">{it.description || it.instructions}</div>
                  )}
                  {it.contraindications?.length > 0 && (
                    <div className="text-[11px] text-amber-300 mt-1">
                      Precauções profissionais: {it.contraindications.join(" · ")}
                    </div>
                  )}
                </div>
                <PlusCircle className="w-5 h-5 text-sky-400 shrink-0" />
              </div>
            </button>
          ))}
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" className="text-slate-400" onClick={() => onOpenChange?.(false)}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
